/**
 * Supabase Storage Image Transformation Utilities
 *
 * Provides functions to generate optimized image URLs using Supabase's
 * built-in image transformation capabilities.
 *
 * @see https://supabase.com/docs/guides/storage/serving/image-transformations
 */

export type ImageSize = 'thumbnail' | 'small' | 'medium' | 'large' | 'full';

export interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number;
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Predefined size presets for common use cases
 */
const SIZE_PRESETS: Record<ImageSize, ImageTransformOptions> = {
  // Thumbnails and icons (48-64px) - for list items, small avatars
  thumbnail: {
    height: 64,
    quality: 80,
    resize: 'contain'
  },
  // Small logos (96-128px) - for navigation headers, cards
  small: {
    height: 128,
    quality: 80,
    resize: 'contain'
  },
  // Medium logos (160-200px) - for organization cards, feature displays
  medium: {
    width: 200,
    height: 200,
    quality: 80,
    resize: 'contain'
  },
  // Large logos (300-400px) - for hero sections, detailed views
  large: {
    width: 400,
    height: 400,
    quality: 80,
    resize: 'contain'
  },
  // Full size - no transformation
  full: {}
};

/**
 * Maximum file size for uploads (2MB)
 */
export const MAX_UPLOAD_SIZE_BYTES = 2 * 1024 * 1024; // 2MB
export const MAX_UPLOAD_SIZE_MB = 2;

/**
 * Maximum image dimensions for uploads
 */
export const MAX_IMAGE_DIMENSION = 2000; // 2000px max width or height

/**
 * Supported image formats for upload
 */
export const SUPPORTED_IMAGE_FORMATS = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

/**
 * Check if a URL is a Supabase Storage URL
 */
export function isSupabaseStorageUrl(url: string): boolean {
  return url.includes('.supabase.co/storage/v1/object/public/');
}

/**
 * Apply transformation parameters to a Supabase Storage URL
 */
export function applyTransformation(
  url: string,
  options: ImageTransformOptions
): string {
  // Only transform Supabase Storage URLs
  if (!isSupabaseStorageUrl(url)) {
    return url;
  }

  // If no transformations specified, return original URL
  if (!options.width && !options.height && !options.quality) {
    return url;
  }

  // Convert /object/public/ to /render/image/public/ for transformations
  const transformUrl = url.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );

  // Build transformation parameters
  const params = new URLSearchParams();

  if (options.width) {
    params.append('width', options.width.toString());
  }

  if (options.height) {
    params.append('height', options.height.toString());
  }

  if (options.quality) {
    params.append('quality', options.quality.toString());
  }

  if (options.resize) {
    params.append('resize', options.resize);
  }

  // Append parameters to URL
  const separator = transformUrl.includes('?') ? '&' : '?';
  return `${transformUrl}${separator}${params.toString()}`;
}

/**
 * Get a transformed image URL using a preset size
 */
export function getTransformedImageUrl(
  url: string | undefined,
  size: ImageSize = 'medium'
): string | undefined {
  if (!url) return undefined;

  const preset = SIZE_PRESETS[size];
  return applyTransformation(url, preset);
}

/**
 * Validate an image file for upload
 * Returns null if valid, error message if invalid
 */
export function validateImageFile(file: File): string | null {
  // Check file size
  if (file.size > MAX_UPLOAD_SIZE_BYTES) {
    return `Image file size must be less than ${MAX_UPLOAD_SIZE_MB}MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`;
  }

  // Check file type
  if (!SUPPORTED_IMAGE_FORMATS.includes(file.type)) {
    return `Unsupported image format. Please upload PNG, JPG, or WebP images.`;
  }

  return null;
}

/**
 * Validate image dimensions (requires creating an Image element)
 * Returns a promise that resolves to null if valid, error message if invalid
 */
export function validateImageDimensions(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      if (img.width > MAX_IMAGE_DIMENSION || img.height > MAX_IMAGE_DIMENSION) {
        resolve(
          `Image dimensions must be less than ${MAX_IMAGE_DIMENSION}px on any side. ` +
          `Current dimensions: ${img.width}x${img.height}px`
        );
      } else {
        resolve(null);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve('Failed to load image for validation');
    };

    img.src = url;
  });
}

/**
 * Validate image file completely (size, format, and dimensions)
 */
export async function validateImageFileComplete(file: File): Promise<string | null> {
  // First check file properties
  const fileError = validateImageFile(file);
  if (fileError) return fileError;

  // Then check dimensions
  const dimensionError = await validateImageDimensions(file);
  if (dimensionError) return dimensionError;

  return null;
}

/**
 * Format bytes to human-readable size
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}
